import { Button } from "@growtherapy/sprout-ui";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { VisitorNameInput } from "./VisitorNameInput";

import { useTranslate } from "@growtherapy/i18n";

export interface InputControlsProps {
  autoFocus?: boolean;
  buttonLabel?: string;
  buttonClassName?: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  inputLabel?: string;
  isRequired: boolean;
  onEnterIsDisabledChange?: (isDisabled: boolean) => void;
  onEnterWaitingRoom?: () => void;
  setVisitorName: (visitorName: string) => void;
  visitorName: string;
  /** @deprecated */
  showWaitingRoomButton?: boolean;
}

export function InputControls({
  autoFocus,
  buttonLabel,
  buttonClassName,
  children,
  className,
  disabled,
  inputLabel,
  isRequired,
  onEnterIsDisabledChange,
  onEnterWaitingRoom,
  setVisitorName: setVisitorNameProp,
  visitorName,
  showWaitingRoomButton = false,
}: InputControlsProps) {
  const t = useTranslate();
  const [visitorNameInputError, setVisitorNameInputError] = useState("");
  const isErrorBlockingSubmit =
    visitorNameInputError ===
      t("telehealth-core:formErrorsCore.thisFieldIsRequiredPleaseEnter") ||
    (isRequired && visitorName.trim().length === 0);

  useEffect(
    function updateEnterIsDisabled() {
      onEnterIsDisabledChange?.(isErrorBlockingSubmit);
    },
    [isErrorBlockingSubmit, onEnterIsDisabledChange],
  );

  const setVisitorName = useCallback(
    (name: string, validationResult: true | string) => {
      setVisitorNameProp(name);
      setVisitorNameInputError(
        validationResult !== true ? validationResult : "",
      );
    },
    [setVisitorNameProp],
  );

  return (
    <div className={twMerge("flex flex-col gap-y-4", className)}>
      <VisitorNameInput
        autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
        className="w-full"
        disabled={disabled}
        isRequired={isRequired}
        label={inputLabel}
        setVisitorName={setVisitorName}
        visitorName={visitorName}
        onEnter={onEnterWaitingRoom}
      />
      {showWaitingRoomButton && (
        <Button
          disabled={isErrorBlockingSubmit}
          onClick={onEnterWaitingRoom}
          type="submit"
          use="secondary"
          className={twMerge("w-full", buttonClassName)}
        >
          {buttonLabel ?? t("telehealth-core:introRoom.enterWaitingRoom")}
        </Button>
      )}
      {children}
    </div>
  );
}
